import { DisplayData, TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import TwtJob from '~/tenants/twt/model/TwtJob';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { DeliverableType } from '~common/model/Deliverable';
import TwtTwilightConfig, { TwtTwilightType } from '~/tenants/twt/performable/twilight/TwtTwilightConfig';
import { TWT_PHOTO_TWILIGHT } from '~/tenants/twt/performable/photo/TwtPhotoJob';
import { OrderCustomTag } from '~/lib/schema/order';

export const TWT_TWILIGHT_PRICE = 249;

export class TwtTwilightJob extends TwtJob<typeof TwtTwilightConfig> {
  get performable() {
    return TwtTwilightConfig;
  }

  get deliverable(): DeliverableType {
    return DeliverableType.PHOTO;
  }

  get twilight() {
    return true;
  }

  get tags(): OrderCustomTag[] {
    return [
      {
        name: 'Twilight',
        color: 'indigo',
        provider: true,
      },
    ];
  }

  info(): DisplayData[] {
    const info = super.info();

    // Which side to shoot, and what the agent asked for: the provider's to
    // read. Neither row was flagged `provider`, so neither reached the Travel
    // tab or the Schedule tab's job view (provider_job_summary), and the
    // instructions had no row at all: the order form took them and nothing
    // showed them to anyone.
    //
    // `invite: false`: both are new to the provider rows, and Twilight has
    // accepted appointments whose calendar invites would otherwise be re-sent
    // for the changed text (DisplayData.invite). So the invite's own job rows
    // stay as they were, for old and new appointments alike.
    info.push({
      name: 'Type',
      value:
        this.metadata.type === TwtTwilightType.FRONT
          ? 'Front'
          : this.metadata.type === TwtTwilightType.BACK
          ? 'Back'
          : 'Front/Back',
      provider: true,
      invite: false,
    });

    // Trimmed: the field is free text, and a stray space or newline would
    // otherwise show a heading with nothing under it.
    const instructions = this.metadata.instructions?.trim();

    if (instructions) {
      info.push({
        name: 'Special Instructions',
        value: instructions,
        provider: true,
        invite: false,
      });
    }

    return info;
  }

  onsite() {
    if(this.order.metadata.transfer){
      return 0;
    }

    return 30;
  }

  get short() {
    return 'NIGHT';
  }

  defaultValue(): ZodVersionedMetadata<(typeof TwtTwilightConfig)['schema']> {
    // TODO: type fix?
    return {
      version: schema_latest_version(TwtTwilightConfig.schema),
    } as any;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  get configurable() {
    return true;
  }

  get eligible() {
    return !this.order.jobs.some((j) => j.performable_id === 'photo' && j.metadata.twilight);
  }

  expenseLines(): TenantOrderLine[] {
    const merged = this.appointment?.jobs.some((j) => j.performable_id === 'photo');

    return [
      {
        amount: new Big(merged ? 150 : 190),
        description: merged ? 'Twilight Shoot' : 'Up to 10 Photos',
        id: 'photos',
      },
    ];
  }

  revenueLines(): TenantOrderLine[] {
    const merged = this.appointment?.jobs.some((j) => j.performable_id === 'photo');

    return [
      {
        amount: new Big(merged ? TWT_PHOTO_TWILIGHT : TWT_TWILIGHT_PRICE),
        taxable: true,
        description: merged ? 'Twilight Shoot' : 'Up to 10 Photos',
        id: 'photos',
      },
    ];
  }
}
